import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Printer, Flame, Layers, Shirt, Upload, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { useContent, parseList } from "@/lib/content";

const GALLERY_CFG = [
  { imgKey: "images.maker_bench" },
  { imgKey: "images.maker_printer" },
  { imgKey: "images.maker_ender" },
  { imgKey: "images.maker_workshop" },
  { imgKey: "images.maker_circuit" },
];

const SERVICE_ICONS = [Printer, Flame, Layers, Shirt];

export default function Maker() {
  const [serviceId, setServiceId] = useState("3D Printing");
  const [material, setMaterial] = useState("");
  const [qty, setQty] = useState(1);
  const [desc, setDesc] = useState("");
  const [files, setFiles] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const content = useContent();
  const galleryRows = parseList(content["maker.gallery"]);
  const gallery = GALLERY_CFG.map((g, i) => ({ img: content[g.imgKey], title: galleryRows[i]?.[0] || "", tag: galleryRows[i]?.[1] || "" }));
  const services = parseList(content["maker.services"]).filter((r) => r[0]).map((r, i) => ({
    id: r[0], base: parseInt(r[1]) || 0, per: parseInt(r[2]) || 0, unit: r[3] || "",
    desc: r[4] || "", guide: r[5] || "", icon: SERVICE_ICONS[i] || Printer,
  }));
  const service = services.find((s) => s.id === serviceId) || services[0] || { id: "", base: 0, per: 0, unit: "", desc: "", guide: "", icon: Printer };
  const mh1 = (content["maker.h1"] || "").split("|");

  const estimate = useMemo(() => service.base + service.per * qty, [service, qty]);

  const submit = async () => {
    if (!contact.name || !contact.email) return toast.error("Name and email are required.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/submissions/maker-project", {
        ...contact, service: service.id, material, quantity: qty,
        description: desc, estimate, file_names: files,
      });
      setResult(data);
      toast.success("Project submitted! Our makers will review your files.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="maker-page">
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-bg" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.35em] text-neon-magenta">{content["maker.eyebrow"]}</p>
          <h1 className="max-w-3xl font-display text-4xl font-black uppercase tracking-tight text-white sm:text-6xl" data-testid="maker-heading">
            {mh1[0]} <span className="text-neon-magenta glow-magenta">{mh1[1]}</span> {mh1[2]}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-300 sm:text-lg">
            {content["maker.intro"]}
          </p>
        </div>
      </section>

      {/* GALLERY */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="maker-gallery-section">
        <h2 className="mb-10 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{content["maker.gallery_h"]}</h2>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {gallery.map((g, i) => (
            <motion.div key={g.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="card-hover group relative overflow-hidden rounded-xl border border-white/10" data-testid={`gallery-${g.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <img src={g.img} alt={g.title} loading="lazy" className="h-56 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <span className="font-mono text-[10px] uppercase tracking-widest text-neon-magenta">{g.tag}</span>
                <p className="font-display text-sm font-bold uppercase text-white">{g.title}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SERVICES + ESTIMATOR */}
      <section id="estimator" className="scroll-mt-24 border-t border-white/10 bg-inkalt/40" data-testid="maker-estimator-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <p className="mb-2 font-mono text-xs uppercase tracking-[0.35em] text-neon-magenta">{content["maker.estimator_eyebrow"]}</p>
          <h2 className="mb-10 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{content["maker.estimator_h"]}</h2>

          {result ? (
            <div className="glass mx-auto max-w-2xl rounded-xl p-10 text-center" data-testid="maker-project-success">
              <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-neon-green" />
              <h3 className="font-display text-2xl font-extrabold uppercase text-white">Project Submitted</h3>
              <p className="mt-2 text-sm text-slate-400">Reference ID:</p>
              <p className="mt-2 select-all rounded-md bg-ink px-4 py-2 font-mono text-sm text-neon-magenta" data-testid="maker-project-reference">{result.id}</p>
              <p className="mt-4 text-sm text-slate-400">We'll email your final quote within 1–2 business days.</p>
            </div>
          ) : (
            <div className="grid gap-8 lg:grid-cols-2">
              <div className="space-y-3">
                {services.map((s) => (
                  <button key={s.id} onClick={() => setServiceId(s.id)} data-testid={`maker-service-${s.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`card-hover w-full rounded-xl border p-5 text-left transition-colors ${serviceId === s.id ? "border-neon-magenta bg-neon-magenta/10" : "border-white/10 bg-inkalt"}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <s.icon className={`mt-1 h-6 w-6 ${serviceId === s.id ? "text-neon-magenta" : "text-slate-400"}`} />
                        <div>
                          <p className="font-display text-base font-extrabold uppercase text-white">{s.id}</p>
                          <p className="mt-1 text-xs leading-relaxed text-slate-400">{s.desc}</p>
                          <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-neon-cyan">{s.guide}</p>
                        </div>
                      </div>
                      <p className="shrink-0 font-mono text-xs text-neon-gold">
                        {s.base > 0 ? `$${s.base} setup + ` : ""}${s.per} {s.unit}
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              <div className="glass rounded-xl p-6 sm:p-8">
                <div className="mb-5 rounded-lg border border-neon-magenta/30 bg-neon-magenta/5 p-4 font-mono text-sm" data-testid="maker-estimate-panel">
                  <div className="flex justify-between text-slate-300"><span>{service.id}</span><span>{service.base > 0 ? `$${service.base} + ` : ""}${service.per} × {qty}</span></div>
                  <div className="mt-2 flex justify-between border-t border-neon-magenta/20 pt-2 text-lg font-bold text-neon-magenta">
                    <span>ESTIMATE</span><span data-testid="maker-estimate-total">${estimate}</span>
                  </div>
                  <p className="mt-2 text-[10px] text-slate-500">Ballpark only — final quote after file review. Bulk discounts at 25+ items.</p>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-400">Material / garment</Label>
                    <Input value={material} onChange={(e) => setMaterial(e.target.value)} data-testid="maker-material-input"
                      placeholder="e.g. PETG black, birch plywood, Gildan hoodie" className="border-white/10 bg-ink text-white" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Quantity</Label>
                    <Input type="number" min={1} value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                      data-testid="maker-quantity-input" className="border-white/10 bg-ink text-white" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Project details</Label>
                    <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} data-testid="maker-description-input"
                      placeholder="Dimensions, colors, deadline, what it's for..." className="border-white/10 bg-ink text-white" />
                  </div>
                  <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-white/20 p-4 text-sm text-slate-400 transition-colors hover:border-neon-magenta/50"
                    data-testid="maker-file-dropzone">
                    <Upload className="h-4 w-4" />
                    {files.length ? files.join(", ") : "Upload STL, vector, or logo files"}
                    <input type="file" multiple className="hidden" data-testid="maker-file-input"
                      onChange={(e) => setFiles(Array.from(e.target.files).map((f) => f.name))} />
                  </label>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="maker-name-input" placeholder="Name *" className="border-white/10 bg-ink text-white" />
                    <Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="maker-email-input" placeholder="Email *" className="border-white/10 bg-ink text-white" />
                  </div>
                  <button onClick={submit} disabled={submitting} data-testid="maker-project-submit-button"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-neon-magenta px-6 py-3 font-display text-xs font-extrabold uppercase tracking-wide text-white transition-colors hover:bg-neon-pink disabled:opacity-60">
                    {submitting ? "Sending..." : "Submit Project"} <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
